import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import { Link as RouterLink } from 'react-router-dom';
import { eventsWithoutPrefixes } from '@rossum/api-client/hooks';
import { Filters } from '@rossum/components/filtering';
import { HeaderTitle } from '@rossum/components/header/Header';
import { PageLayout } from '@rossum/components/page-layout/PageLayout';
import { PageSizeSelect } from '@rossum/components/page-size-select/PageSizeSelect';
import SearchInput from '@rossum/components/search-input/SearchInput';
import { IconCirclePlus, IconInfoSquareRounded } from '@rossum/ui/icons/tabler';
import {
  Button,
  Chip,
  Pagination,
  Paper,
  Stack,
  SvgIcon,
  Tooltip,
} from '@rossum/ui/material';
import InfoBox from '../../../../components/UI/InfoBox/index';
import {
  FUNCTIONS_LINK,
  pageSizeOptions,
  WEBHOOKS_LINK,
} from '../../../../constants/values';
import { linebreak, link } from '../../../../lib/formaterValues';
import { HOOK_LIST_QUERY } from '../../../../redux/modules/localStorage/actions';
import { isTrialSelector } from '../../../../redux/modules/organization/selectors';
import { updateUiSettings } from '../../../../redux/modules/user/actions';
import { displayExtensionsIntroBannerSelector } from '../../../../redux/modules/user/selectors';
import { State } from '../../../../types/state';
import { useStoredQuery } from '../../../../utils/hooks/useStoredQuery';
import { UnpaidFeatureOverlay } from '../../../pricing/components/UnpaidFeatureOverlay';
import { useExtensionsFeatureSubscription } from '../../../pricing/hooks/useExtensionsFeatureSubscription';
import { useWorkspacesWithQueues } from '../../../queues/hooks/useWorkspacesWithQueues';
import { ExtensionsHeader } from '../../components/ExtensionsHeader';
import { useHooks } from '../../hooks/useHooks';
import { extensionsCreatePath } from '../../routes';
import { IntroToExtensions } from './components/IntroToExtensions';
import { ViewSwitcher } from './components/ViewSwitcher';
import { DependenciesGraph } from './dependencies-graph';
import { useGraphHooks } from './dependencies-graph/hooks/useGraphHooks';
import { ExtensionsList } from './extensions-list/ExtensionsList';
import {
  extensionQueryParser,
  useExtensionsFilterSettings,
} from './hooks/useExtensionsFilterSettings';

type StateProps = {
  displayIntroBanner: boolean;
  isTrial: boolean;
};

type DispatchProps = {
  setHideIntroBanner: () => void;
};

type Props = StateProps & DispatchProps;

const Extensions = ({
  displayIntroBanner,
  isTrial,
  setHideIntroBanner,
}: Props) => {
  const { data: activeHooks } = useHooks({
    active: true,
  });

  const activeExtensionsCount = activeHooks?.pagination.total;

  const [parsedParams, updateParsedParams] = useStoredQuery({
    parser: extensionQueryParser,
    localStorageKey: HOOK_LIST_QUERY,
  });

  const hookQuery = {
    pageSize: parsedParams.pageSize,
    page: parsedParams.page,
    search: parsedParams.search,
    queue: parsedParams.queues,
    events: parsedParams.events,
  };

  const { data: extensions, isSuccess: extensionsLoaded } = useHooks(hookQuery);

  // Technically, we don't need all queues but they will be loaded in the select component anyways.
  const {
    queues,
    workspaces,
    workspacesWithQueues,
    isLoading: isQueuesLoading,
  } = useWorkspacesWithQueues({
    enableQueries: true,
  });

  const filterSettings = useExtensionsFilterSettings({
    queues,
    workspaces,
    isQueuesLoading,
    events: eventsWithoutPrefixes,
    setFilters: updateParsedParams,
    showEventsFilter: parsedParams.view === 'list',
  });

  const filtersActive =
    parsedParams.queues !== undefined ||
    parsedParams.events !== undefined ||
    !!parsedParams.search;

  const displaySearch =
    parsedParams.view === 'graph'
      ? false
      : !!extensions?.results.length || filtersActive || !extensionsLoaded;

  const extensionsSubscription = useExtensionsFeatureSubscription();
  const isFeatureEnabled = extensionsSubscription.canAccess;

  const intl = useIntl();

  const {
    isLoading,
    isFetching,
    graphDataGroupedPerEvents,
    originalExtensions,
  } = useGraphHooks({
    queueId: parsedParams.queues,
    enabled: parsedParams.view === 'graph',
  });

  return (
    <PageLayout
      fullWidth={isFeatureEnabled}
      renderHeader={params => (
        <ExtensionsHeader
          {...params}
          value="myExtensions"
          title={hasScrolled => (
            <Stack direction="row" alignItems="center" gap={1}>
              <HeaderTitle
                hasScrolled={hasScrolled}
                title={intl.formatMessage({
                  id: `containers.settings.sidebar.myExtensions`,
                })}
              />
              {activeExtensionsCount !== undefined && (
                <Chip
                  label={`${activeExtensionsCount}`}
                  data-cy="extensions-active-count"
                />
              )}
              <Tooltip
                placement="top"
                title={intl.formatMessage(
                  {
                    id: 'containers.settings.extensions.helpTooltip',
                  },
                  {
                    linebreak,
                    webhooksLink: link(WEBHOOKS_LINK),
                    functionsLink: link(FUNCTIONS_LINK),
                  }
                )}
              >
                <SvgIcon fontSize="medium" color="secondary">
                  <IconInfoSquareRounded />
                </SvgIcon>
              </Tooltip>
            </Stack>
          )}
          buttons={hasScrolled =>
            isFeatureEnabled
              ? [
                  <Stack
                    key="extension-buttons"
                    gap={1}
                    sx={{
                      alignItems: 'flex-end',
                    }}
                  >
                    {!hasScrolled && (
                      <ViewSwitcher
                        activeView={parsedParams.view}
                        onViewChange={value =>
                          updateParsedParams({ view: value, page: 1 })
                        }
                      />
                    )}
                    <Stack direction="row" alignItems="center" gap={1}>
                      {displaySearch && (
                        <SearchInput
                          placeholder={intl.formatMessage({
                            id: 'containers.settings.extensions.search',
                          })}
                          value={parsedParams.search}
                          onChange={search =>
                            updateParsedParams({
                              search: search || undefined,
                              page: 1,
                            })
                          }
                        />
                      )}
                      <Tooltip
                        title={
                          extensionsSubscription.purchased
                            ? ''
                            : intl.formatMessage({
                                id: 'containers.settings.extensions.createExtensionDisabled',
                              })
                        }
                      >
                        <span>
                          <Button
                            component={RouterLink}
                            disabled={!extensionsSubscription.purchased}
                            to={extensionsCreatePath()}
                            variant="contained"
                            startIcon={
                              <SvgIcon fontSize="medium">
                                <IconCirclePlus />
                              </SvgIcon>
                            }
                            data-cy="extensions-add-extension"
                            sx={{
                              '&:hover': {
                                color: theme =>
                                  theme.palette.primary.contrastText,
                              },
                            }}
                          >
                            {intl.formatMessage({
                              id: 'containers.settings.extensions.createExtension',
                            })}
                          </Button>
                        </span>
                      </Tooltip>
                    </Stack>
                  </Stack>,
                ]
              : []
          }
        />
      )}
      renderFooter={() => {
        if (parsedParams.view !== 'list' || !isFeatureEnabled) return null;
        return (
          <Stack
            direction="row"
            alignItems="center"
            spacing={3}
            p={2.5}
            alignSelf="flex-end"
          >
            <PageSizeSelect
              options={pageSizeOptions}
              setPageSize={(pageSize: number) =>
                updateParsedParams({ pageSize, page: 1 })
              }
              pageSize={parsedParams.pageSize}
            />
            {extensions && (
              <Pagination
                page={parsedParams.page}
                count={extensions.pagination.totalPages}
                onChange={(_, page: number) => updateParsedParams({ page })}
              />
            )}
          </Stack>
        );
      }}
    >
      {isFeatureEnabled ? (
        <Stack spacing={3} p={4}>
          <Filters
            dataCy="extensions-filters"
            filters={parsedParams}
            setFilters={updateParsedParams}
            filterSettings={filterSettings}
          />
          {isTrial && (
            <InfoBox
              title="containers.settings.extensions.upgrade.title"
              text="containers.settings.extensions.upgrade.text"
            />
          )}
          {displayIntroBanner && (
            <IntroToExtensions
              dataCy="extensions-intro-banner"
              onClose={setHideIntroBanner}
            />
          )}
          {parsedParams.view === 'list' ? (
            <ExtensionsList
              hookQuery={hookQuery}
              extensions={extensions?.results ?? []}
              extensionsLoaded={extensionsLoaded}
              filtersActive={filtersActive}
              workspacesWithQueues={workspacesWithQueues}
            />
          ) : (
            <DependenciesGraph
              isLoading={isLoading}
              isFetching={isFetching}
              graphDataGroupedPerEvents={graphDataGroupedPerEvents}
              selectedQueueId={parsedParams.queues}
              originalExtensions={originalExtensions}
            />
          )}
        </Stack>
      ) : (
        <Stack component={Paper} m={4} p={4} justifyContent="center">
          <UnpaidFeatureOverlay
            title={intl.formatMessage({
              id: 'features.pricing.unpaidFeatureOverlay.title.extensions',
            })}
            dataCy="unpaid-overlay-extensions"
          />
        </Stack>
      )}
    </PageLayout>
  );
};

const mapStateToProps = (state: State): StateProps => ({
  displayIntroBanner: displayExtensionsIntroBannerSelector(state),
  isTrial: !!isTrialSelector(state),
});

const mapDispatchToProps: DispatchProps = {
  setHideIntroBanner: () =>
    updateUiSettings({
      hideExtensionsIntroBanner: true,
    }),
};

export const ExtensionsPage = connect<
  StateProps,
  DispatchProps,
  Record<string, never>,
  State
>(
  mapStateToProps,
  mapDispatchToProps
)(Extensions);
